/**
 * Canonical breakdown-item label renderer.
 *
 * Handles three input shapes:
 *   1. Plain string ("fishing boat")
 *   2. Structured object { text, en, confidence }
 *   3. Python-dict-string repr: "{'text': '...', 'en': '...', 'confidence': 1.0}"
 *
 * Also detects PDF-CID-corrupted Malayalam in `text` and falls back to `en`.
 *
 * Corruption detector rules (any single rule fires → string is corrupt):
 *   A. Sentinel char anywhere: # $ ? U+FFFD U+25CC
 *   B. Stranded virama (U+0D4D) at word boundary (followed by whitespace or EOS)
 *   C. Mixed Malayalam + Latin in same string — UNLESS it matches the safe
 *      annotation shape  "<ml> (<latin>)" exactly (e.g. "ബോട്ട് (boat)")
 *   D. Malayalam consonant (U+0D15..U+0D39) followed by whitespace
 *      followed by a vowel sign / matra (U+0D3E..U+0D4D / U+0D57 / U+0D62..U+0D63).
 *      A vowel sign separated from its base consonant by whitespace is broken
 *      shaping. Catches "പ യാേനാ", "ക# ൽ", "തലയ ണ".
 *   E. String starts with an orphan vowel sign (no base consonant preceding it).
 *
 * NEVER mutates backing data. Read-side transform only.
 */

const MALAYALAM_RE  = /[\u0d00-\u0d7f]/;
const LATIN_ALNUM   = /[a-zA-Z0-9]/;
// Strict safe-annotation pattern: pure-Malayalam (+ whitespace + punctuation)
// followed by  a parenthesized Latin chunk, optionally with a trailing
// punctuation / whitespace. Allows commas, periods, etc.
const SAFE_ANNOTATION_RE = /^[\u0d00-\u0d7f\s.,;:!?'-]+\(\s*[a-zA-Z][a-zA-Z\s.,;:'-]*\)[\s.,;:!?]*$/;
// Single bare Malayalam consonant token (no attached vowel sign / virama).
// Real Malayalam words almost never appear as a lone consonant — it indicates
// a broken-word fragment like "പ യാേനാ" (should be "പിയാനോ") where the matra
// got detached from its base consonant.
const BARE_CONSONANT_TOKEN_RE = /^[\u0d15-\u0d39]$/;
// Orphan leading vowel sign (no base consonant).
const ORPHAN_LEADING_VOWEL_RE = /^[\u0d3e-\u0d4d\u0d57\u0d62\u0d63]/;

export function isCorruptIndicText(value) {
  if (!value || typeof value !== 'string') return false;

  // Rule A: sentinel chars
  if (/[#$?\ufffd\u25cc]/.test(value)) return true;

  // Pure-ASCII early exit — cannot be "corrupt Malayalam"
  if (!MALAYALAM_RE.test(value)) return false;

  const trimmed = value.trim();

  // Rule B: mixed Malayalam + Latin (unless safe annotation)
  // Word-final virama like "ബോട്ട്" is legitimate Malayalam, so we don't
  // flag it as a sentinel — only the actual mixed-script case.
  if (LATIN_ALNUM.test(value)) {
    if (SAFE_ANNOTATION_RE.test(trimmed)) return false;
    return true;
  }

  // Rule C: Malayalam-only — bare-consonant token (broken word fragment)
  // Catches "പ യാേനാ", "തലയ ണ", "ക S" (the second-token check after the
  // mixed-script bail-out above also handles "S വ").
  const tokens = value.split(/\s+/);
  for (const tok of tokens) {
    if (BARE_CONSONANT_TOKEN_RE.test(tok)) return true;
  }

  // Rule D: Malayalam-only — orphan leading vowel sign
  if (ORPHAN_LEADING_VOWEL_RE.test(trimmed)) return true;

  return false;
}

function pickFromObject(obj, languageMode) {
  if (!obj || typeof obj !== 'object') return '';
  const original = obj.text || obj.name || obj.label || '';
  const english  = obj.en || obj.english || '';
  if (languageMode === 'english' && english) return english;
  if (original && !isCorruptIndicText(original)) return original;
  if (english) return english;                   // fallback when original is corrupt
  return original || '';                          // last resort
}

export function getBreakdownItemLabel(item, languageMode = 'original') {
  if (item === null || item === undefined) return '';
  if (typeof item === 'number' || typeof item === 'boolean') return String(item);

  if (typeof item === 'string') {
    const trimmed = item.trim();
    const looksLikeDictString =
      trimmed.startsWith('{') &&
      (trimmed.includes("'text'") || trimmed.includes('"text"'));
    if (looksLikeDictString) {
      try {
        const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
        return pickFromObject(parsed, languageMode) || item;
      } catch { /* fall through */ }
    }
    return isCorruptIndicText(item) ? '' : item;
  }

  if (typeof item === 'object') {
    return pickFromObject(item, languageMode);
  }
  return String(item);
}

export default getBreakdownItemLabel;
